import { HttpErrorResponse } from '@angular/common/http';
import { EmptyError, TimeoutError } from 'rxjs';
import { AuditTrailEntry } from '../../../../services/workflow-engine/generated/models/audit-trail-entry';
import { AuditTrailResponse } from '../../../../services/workflow-engine/generated/models/audit-trail-response';
import { CaseStateView } from '../../../../services/workflow-engine/generated/models/case-state-view';
import { DecideHttpRequest } from '../../../../services/workflow-engine/generated/models/decide-http-request';
import { HeldRole } from '../../../../services/workflow-engine/generated/models/held-role';
import { MeRolesResponse } from '../../../../services/workflow-engine/generated/models/me-roles-response';
import { StepForm } from '../../../../services/workflow-engine/generated/models/step-form';
import { StepFormField } from '../../../../services/workflow-engine/generated/models/step-form-field';
import { WorkflowGraph } from '../../../../services/workflow-engine/generated/models/workflow-graph';
import type { LifecycleRailStage } from '../components/uc2-lifecycle-rail/uc2-lifecycle-rail.component';

/** The engine marks the `returned` edge with this route; it loops back to a step already visited. */
const PRIOR_OWNER_ROUTE = 'prior-owner';

/** The outcome that sends a step back instead of deciding it. */
export const RETURNED_OUTCOME = 'returned';

export type GraphNodeStatus = 'done' | 'active' | 'upcoming' | 'skipped';

export interface GraphNodeView {
  id: string;
  title: string;
  kind: string;
  role: string | null;
  status: GraphNodeStatus;
  /** Outcomes that lead forward; `returned` loops are drawn as a note, not a step. */
  outcomes: string[];
  canReturn: boolean;
}

/** The published graph in reading order with this case's progress overlaid. */
export function buildGraphView(
  graph: WorkflowGraph,
  state: CaseStateView,
  audit: AuditTrailResponse,
): GraphNodeView[] {
  const nodesById = new Map(graph.nodes.map(node => [node.id, node]));
  const start = graph.nodes.find(node => node.start) ?? graph.nodes[0];
  if (!start) {
    return [];
  }

  const forward = (from: string) => graph.edges.filter(edge => edge.from === from && edge.route !== PRIOR_OWNER_ROUTE);
  const ordered: string[] = [];
  const queue = [start.id];
  while (queue.length > 0) {
    const id = queue.shift() as string;
    if (ordered.includes(id) || !nodesById.has(id)) {
      continue;
    }
    ordered.push(id);
    forward(id).forEach(edge => queue.push(edge.to));
  }

  // Breadth-first order reaches an end step through the shortest path; end steps read last.
  ordered.sort((a, b) => Number(nodesById.get(a)!.kind === 'end') - Number(nodesById.get(b)!.kind === 'end'));

  // Replay the trail in order: a step is done once the case moved on from it, and a return reopens
  // the step it returns to and everything downstream of it.
  const downstream = (from: string): Set<string> => {
    const reached = new Set<string>();
    const pending = [from];
    while (pending.length > 0) {
      const id = pending.pop() as string;
      if (!reached.has(id)) {
        reached.add(id);
        forward(id).forEach(edge => pending.push(edge.to));
      }
    }
    return reached;
  };
  const completed = new Set<string>();
  const reached = new Set<string>();
  audit.entries.forEach(entry => {
    const from = entry.fromStepKey;
    const to = entry.toStepKey;
    if (!from || !to || from === to) {
      return;
    }
    const isReturn = graph.edges.some(edge => edge.from === from && edge.to === to && edge.route === PRIOR_OWNER_ROUTE);
    if (isReturn) {
      downstream(to).forEach(id => {
        completed.delete(id);
        reached.delete(id);
      });
    } else {
      completed.add(from);
      reached.add(to);
    }
  });
  const activeKey = state.activeStep?.stepKey ?? null;
  const closed = state.status !== 'open';
  // An end step is never left, so it is done once the case reached it and closed.
  const isDone = (id: string) =>
    completed.has(id) || (closed && reached.has(id) && nodesById.get(id)!.kind === 'end');

  return ordered.map(id => {
    const node = nodesById.get(id)!;
    let status: GraphNodeStatus;
    if (id === activeKey) {
      status = 'active';
    } else if (isDone(id)) {
      status = 'done';
    } else {
      status = closed ? 'skipped' : 'upcoming';
    }
    return {
      id,
      title: node.title ?? id,
      kind: node.kind,
      role: node.role ?? node.body?.role ?? null,
      status,
      outcomes: forward(id).map(edge => edge.outcome),
      canReturn: graph.edges.some(edge => edge.from === id && edge.route === PRIOR_OWNER_ROUTE),
    };
  });
}

/** Role keys the user holds for a case type, from the engine's role grants. */
export function heldRoles(response: MeRolesResponse, caseType: string): HeldRole[] {
  return response.caseTypes.find(entry => entry.caseType === caseType)?.roles ?? [];
}

/** Only human actors are Graph users; service events can still record a human owner. */
export function auditPersonOids(entries: readonly AuditTrailEntry[]): string[] {
  return entries
    .flatMap(entry => entry.actorType === 'human'
      ? [entry.actorOid, entry.ownerOidAtTime]
      : [entry.ownerOidAtTime])
    .filter((oid): oid is string => !!oid);
}

/**
 * Who a return to `target` goes back to: the owner recorded when that step was last entered, as the engine reads it.
 * The engine still checks that person holds the role and the membership, and queues the case otherwise.
 */
export function priorOwnerOid(entries: readonly AuditTrailEntry[], target: string): string | null {
  const latest = entries
    .filter(entry => entry.eventKind === 'step-entered' && entry.fromStepKey === target && entry.ownerOidAtTime)
    .reduce<AuditTrailEntry | null>(
      (best, entry) => (!best || entry.occurredOnUtc > best.occurredOnUtc ? entry : best),
      null,
    );
  return latest?.ownerOidAtTime ?? null;
}

export interface FormFieldView {
  /** Form control key: `record__name`; a dot is not allowed in a form group key. */
  key: string;
  record: string;
  name: string;
  label: string;
  type: 'text' | 'vocab' | 'datetime' | 'ref' | 'other';
  values: string[];
  required: boolean;
  requiredWhen: string | null;
  ref: string | null;
  /** The engine's field definition, including any nested `of`, `fields`, and `minItems`. */
  source: StepFormField;
}

export function labelFor(name: string): string {
  const spaced = name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/[_-]/g, ' ').toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** `io-production-engineer` as `Production engineer`; a role read from each action item has no fixed name. */
export function roleLabel(role: string | null): string {
  if (!role) {
    return 'Unassigned';
  }
  return role.startsWith('item.') ? 'Per action item' : labelFor(role.replace(/^io-/, ''));
}

/** The graph as lifecycle rail stages, so the engine's real steps are drawn the way the prototype drew them. */
export function toRailStages(nodes: readonly GraphNodeView[], showReturnHint = true): LifecycleRailStage[] {
  return nodes.map(node => ({
    label: node.title,
    status: node.status === 'active' ? 'current' : node.status === 'upcoming' ? 'pending' : node.status,
    detail: [node.role ? roleLabel(node.role) : null, showReturnHint && node.canReturn ? 'can return' : null]
      .filter(Boolean)
      .join(' \u00b7 '),
  }));
}

function fieldType(field: StepFormField): FormFieldView['type'] {
  return field.type === 'text' || field.type === 'vocab' || field.type === 'datetime' || field.type === 'ref'
    ? field.type
    : 'other';
}

/** The inputs a step asks for, one per record field. */
export function buildFieldViews(form: StepForm): FormFieldView[] {
  return form.captures.flatMap(capture =>
    capture.fields.map(field => ({
      key: `${capture.record}__${field.name}`,
      record: capture.record,
      name: field.name,
      label: field.name === 'assessedAssessment' ? 'Class assessment' : labelFor(field.name),
      type: fieldType(field),
      values: field.values ?? [],
      required: field.required === true,
      requiredWhen: field.requiredWhen ?? null,
      ref: field.ref ?? null,
      source: field,
    })),
  );
}

type Truth = true | false | 'unknown';

function operand(token: string, values: Record<string, string>): string | null | 'unknown' {
  if (token.startsWith('caseContext.')) {
    return 'unknown';
  }
  if (token === 'null') {
    return null;
  }
  const text = values[token];
  return text === undefined || text === '' ? null : text;
}

/** Evaluates `a == B && c != D` over this step's fields; an operand read from the case context is unknown. */
function evaluate(expression: string, values: Record<string, string>): Truth {
  let result: Truth = true;
  for (const clause of expression.split('&&').map(part => part.trim())) {
    const match = /^(\S+)\s*(==|!=)\s*(\S+)$/.exec(clause);
    if (!match) {
      return 'unknown';
    }
    const [, left, op, right] = match;
    const leftValue = operand(left, values);
    const rightValue = right in values || right.startsWith('caseContext.') || right === 'null'
      ? operand(right, values)
      : right;
    let outcome: Truth;
    if (leftValue === 'unknown' || rightValue === 'unknown') {
      outcome = 'unknown';
    } else {
      outcome = op === '==' ? leftValue === rightValue : leftValue !== rightValue;
    }
    if (outcome === false) {
      return false;
    }
    if (outcome === 'unknown') {
      result = 'unknown';
    }
  }
  return result;
}

/** Values are keyed by field name within one record; `record__name` keys are narrowed by the caller. */
function fieldValues(values: Record<string, string>, record: string): Record<string, string> {
  const prefix = `${record}__`;
  return Object.fromEntries(
    Object.entries(values)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  );
}

/** A conditional field is hidden only when its condition is definitely false. */
export function isFieldVisible(field: FormFieldView, values: Record<string, string>): boolean {
  return field.requiredWhen === null || evaluate(field.requiredWhen, fieldValues(values, field.record)) !== false;
}

export function isFieldRequired(field: FormFieldView, values: Record<string, string>): boolean {
  if (field.required) {
    return true;
  }
  return field.requiredWhen !== null && evaluate(field.requiredWhen, fieldValues(values, field.record)) === true;
}

/** The first visible required field still empty, or null when the capture is complete. */
export function missingField(fields: FormFieldView[], values: Record<string, string>): FormFieldView | null {
  return (
    fields.find(
      field => isFieldVisible(field, values) && isFieldRequired(field, values) && !(values[field.key] ?? '').trim(),
    ) ?? null
  );
}

/** The first visible field this screen cannot capture (an array or object value) that the step requires. */
export function unsupportedField(fields: readonly FormFieldView[], values: Record<string, string>): FormFieldView | null {
  return (
    fields.find(
      field =>
        field.type === 'other' &&
        isFieldVisible(field, values) &&
        (field.required || field.requiredWhen !== null),
    ) ?? null
  );
}

const REASON_OUTCOMES: readonly string[] = ['not_applicable', 'cannot_complete'];

/** Whether the engine rejects this outcome without a reason: a return, or a for-each item that did not get done. */
export function outcomeNeedsReason(outcome: string, form: StepForm): boolean {
  if (outcome === RETURNED_OUTCOME) {
    return form.return?.reasonRequired ?? true;
  }
  return REASON_OUTCOMES.includes(outcome);
}

/** The decide request: only fields that are visible and filled are sent, grouped by record. */
export function buildDecideBody(
  fields: FormFieldView[],
  outcome: string,
  values: Record<string, string>,
  reason: string,
): DecideHttpRequest {
  const captures: Record<string, Record<string, string>> = {};
  fields.forEach(field => {
    const value = (values[field.key] ?? '').trim();
    if (value && field.type !== 'other' && isFieldVisible(field, values)) {
      (captures[field.record] ??= {})[field.name] = field.type === 'datetime' ? new Date(value).toISOString() : value;
    }
  });
  const body: DecideHttpRequest = { outcome };
  if (Object.keys(captures).length > 0) {
    body.captures = captures as unknown as DecideHttpRequest['captures'];
  }
  if (reason.trim()) {
    body.reason = reason.trim();
  }
  return body;
}

/** Shown when no answer reached the browser, so the command may or may not have been applied. */
export const COMMAND_UNCONFIRMED_MESSAGE =
  'The workflow engine did not confirm the command. The case has been reloaded; check whether the step changed before trying again.';

/** A short message for a failed engine command; problem details carry the engine's reason. */
export function describeCommandError(error: unknown): string {
  if (error instanceof TimeoutError || error instanceof EmptyError) {
    return COMMAND_UNCONFIRMED_MESSAGE;
  }
  if (!(error instanceof HttpErrorResponse)) {
    return 'The command could not be sent. Try again.';
  }
  const detail = (error.error as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string' && detail) {
    return detail;
  }
  switch (error.status) {
    case 0:
      return COMMAND_UNCONFIRMED_MESSAGE;
    case 403:
      return 'You do not hold the role this step requires.';
    case 409:
      return 'The case changed since you opened it. It has been reloaded.';
    case 422:
      return 'The engine rejected the submitted values.';
    default:
      return 'The command could not be completed. Try again.';
  }
}
